import Axios from "axios";

/**
 * ล็อกอินผ่าน Active Directory
 *
 * คีย์ของ AD ไม่เคยอยู่ในไฟล์ build ของหน้าเว็บ
 * หน้าเว็บส่งแค่ username กับ password ไปที่ backend ของเราเอง
 * แล้ว backend เป็นคนถือคีย์ไปคุยกับ AD ต่อ
 *
 * เหตุผลที่ต้องทำแบบนี้: API ของ AD คืนชื่อ อีเมล และ role ของพนักงานกลับมา
 * แม้ในกรณีที่รหัสผ่านผิด ใครถือคีย์จึงไล่ยิง username เพื่อดูดข้อมูลพนักงาน
 * ทั้งองค์กรได้โดยไม่ต้องรู้รหัสผ่านสักคน
 */

const API_BASE = (process.env.REACT_APP_DOMAIN_SHORTLINK_API || "").replace(/\/+$/, "");
const TOKEN_KEY = "shortlink_session_token";
const USER_KEY = "shortlink_session_user";

export const isConfigured = () => Boolean(API_BASE);

export const getToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY) || "";
  } catch (e) {
    return "";
  }
};

export const getCachedUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem(USER_KEY) || "null");
  } catch (e) {
    return null;
  }
};

function storeSession(token, user) {
  try {
    if (token) {
      sessionStorage.setItem(TOKEN_KEY, token);
      sessionStorage.setItem(USER_KEY, JSON.stringify(user || null));
    } else {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
    }
  } catch (e) {
    /* โหมดส่วนตัวของบางเบราว์เซอร์เขียน sessionStorage ไม่ได้ */
  }
}

export const logout = () => storeSession(null, null);

/** header สำหรับเรียก API ที่ต้องยืนยันตัวตน */
export const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function messageOf(error, fallback) {
  const detail = error && error.response && error.response.data && error.response.data.error;
  if (detail && detail.message) return detail.message;
  if (error && error.message === "Network Error") {
    return "ต่อ API ไม่ได้ ตรวจว่า REACT_APP_DOMAIN_SHORTLINK_API ถูกต้องและ Cloud Run เปิดอยู่";
  }
  return fallback;
}

/**
 * คืน { ok, user, message, status }
 * status = "pending" หมายถึงยืนยันตัวตนผ่านแล้วแต่รอผู้ดูแลอนุมัติ
 */
export async function login(username, password) {
  try {
    const result = await Axios.post(
      `${API_BASE}/api/auth/login`,
      { username: username.trim(), password },
      { headers: { "Content-Type": "application/json" } }
    );
    storeSession(result.data.token, result.data.user);
    return {
      ok: true,
      user: result.data.user,
      passwordExpiresInDays: result.data.passwordExpiresInDays,
    };
  } catch (error) {
    const data = error.response && error.response.data;
    const code = data && data.error && data.error.code;
    return {
      ok: false,
      code,
      status: (data && data.error && data.error.status) || null,
      message: messageOf(error, "เข้าสู่ระบบไม่สำเร็จ"),
    };
  }
}

/** ตรวจว่าโทเคนที่เก็บไว้ยังใช้ได้ ใช้ตอนเปิดหน้าใหม่ */
export async function me() {
  if (!getToken()) return { ok: false };
  try {
    const result = await Axios.get(`${API_BASE}/api/auth/me`, { headers: authHeaders() });
    storeSession(getToken(), result.data.user);
    return { ok: true, appRole: result.data.appRole, user: result.data.user };
  } catch (error) {
    logout();
    return { ok: false, message: messageOf(error, "เซสชันหมดอายุ") };
  }
}

export async function authConfig() {
  try {
    const result = await Axios.get(`${API_BASE}/api/auth/config`);
    return result.data;
  } catch (e) {
    return { adLoginEnabled: false };
  }
}

/* ---------- จัดการผู้ใช้ (เฉพาะ owner) ---------- */

export async function listUsers(params) {
  const result = await Axios.get(`${API_BASE}/api/users`, { headers: authHeaders(), params });
  return result.data;
}

export async function decideUser(username, payload) {
  try {
    const result = await Axios.patch(
      `${API_BASE}/api/users/${encodeURIComponent(username)}`,
      payload,
      { headers: { ...authHeaders(), "Content-Type": "application/json" } }
    );
    return result.data;
  } catch (error) {
    throw new Error(messageOf(error, "เปลี่ยนสิทธิ์ไม่สำเร็จ"));
  }
}

/* ---------- Partner (ระบบภายนอก) เฉพาะ owner ---------- */

export async function listPartners() {
  const result = await Axios.get(`${API_BASE}/api/partners`, { headers: authHeaders() });
  return result.data;
}

export async function createPartner(payload) {
  try {
    const result = await Axios.post(`${API_BASE}/api/partners`, payload, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return result.data;
  } catch (error) {
    throw new Error(messageOf(error, "ออกคีย์ให้ partner ไม่สำเร็จ"));
  }
}

export async function updatePartner(id, payload) {
  try {
    const result = await Axios.patch(`${API_BASE}/api/partners/${id}`, payload, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return result.data;
  } catch (error) {
    throw new Error(messageOf(error, "แก้ไข partner ไม่สำเร็จ"));
  }
}

export async function rotatePartnerKey(id) {
  try {
    const result = await Axios.post(`${API_BASE}/api/partners/${id}/rotate-key`, {}, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return result.data;
  } catch (error) {
    throw new Error(messageOf(error, "ออกคีย์ใหม่ไม่สำเร็จ"));
  }
}

export async function partnerUsage(id) {
  const result = await Axios.get(`${API_BASE}/api/partners/${id}/usage`, { headers: authHeaders() });
  return result.data;
}

export const ROLE_LABEL = {
  viewer: "ดูอย่างเดียว",
  editor: "สร้างและแก้ลิงก์ได้",
  owner: "จัดการผู้ใช้ได้ด้วย",
};

export const STATUS_LABEL = {
  pending: "รออนุมัติ",
  approved: "อนุมัติแล้ว",
  rejected: "ปฏิเสธ",
  disabled: "ปิดใช้งาน",
};
